import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  Chip,
} from '@mui/material';
import { QrCode2, AccessTime, AddCircleOutline } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { GRADIENTS, RADIUS_LIBRARIAN as RADIUS } from '../../../theme/tokens';
import { MOCK_SESSIONS } from '../crmMockData';

const DEMO_NEW_STUDENTS = [
  { studentName: 'Nisha Reddy', seatNo: 'D-01', plan: 'Weekly', initial: 'N' },
  { studentName: 'Vivek Joshi', seatNo: 'C-08', plan: 'Monthly', initial: 'V' },
  { studentName: 'Meera Iyer', seatNo: 'A-12', plan: 'Half-Yearly', initial: 'M' },
  { studentName: 'Sameer Rao', seatNo: 'B-03', plan: 'Monthly', initial: 'S' },
];

let autoId = 100;

function getCheckInNow() {
  const h = new Date().getHours();
  const m = new Date().getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hh = h % 12 || 12;
  return `${String(hh).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
}

function DemoSessionsTab() {
  const [sessions, setSessions] = useState(MOCK_SESSIONS);
  const [nextIdx, setNextIdx] = useState(0);

  const handleCheckIn = () => {
    const template = DEMO_NEW_STUDENTS[nextIdx % DEMO_NEW_STUDENTS.length];
    const newSession = {
      id: `s-new-${autoId++}`,
      studentName: template.studentName,
      seatNo: template.seatNo,
      checkInTime: getCheckInNow(),
      plan: template.plan,
      _isNew: true,
    };
    setSessions((prev) => [newSession, ...prev]);
    setNextIdx((i) => i + 1);
  };

  return (
    <Box>
      {/* Header + check-in button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={800}>
            Active Sessions
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {sessions.length} students currently in
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddCircleOutline sx={{ fontSize: 16 }} />}
          onClick={handleCheckIn}
          sx={{
            background: GRADIENTS.brand,
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: RADIUS.pill,
            px: 2,
            fontSize: '0.75rem',
            '&:hover': { background: GRADIENTS.brandDeep },
          }}
        >
          Check in
        </Button>
      </Box>

      {/* Sessions list */}
      <Card sx={{ borderRadius: RADIUS.xl }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <List disablePadding>
            <AnimatePresence initial={false}>
              {sessions.map((s) => (
                <motion.div
                  key={s.id}
                  layout
                  initial={{ opacity: 0, y: -20, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                >
                  <ListItem
                    divider
                    sx={{
                      px: 2,
                      py: 1.25,
                      bgcolor: s._isNew ? 'rgba(242,101,34,0.05)' : 'transparent',
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        <Avatar
                          sx={{
                            width: 34,
                            height: 34,
                            background: GRADIENTS.brand,
                            fontSize: '0.8rem',
                            fontWeight: 700,
                          }}
                        >
                          {s.studentName.charAt(0)}
                        </Avatar>
                        {s._isNew && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              right: 0,
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: '#16A34A',
                              border: '2px solid',
                              borderColor: 'background.paper',
                              animation: 'pulse 1.8s ease-in-out infinite',
                              '@keyframes pulse': {
                                '0%, 100%': { boxShadow: '0 0 0 0 rgba(22,163,74,0.5)' },
                                '50%': { boxShadow: '0 0 0 5px rgba(22,163,74,0)' },
                              },
                            }}
                          />
                        )}
                      </Box>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {s.studentName}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          <AccessTime sx={{ fontSize: 11, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {s.checkInTime}
                          </Typography>
                          {s._isNew && (
                            <Chip
                              label="Just checked in"
                              size="small"
                              sx={{
                                ml: 0.5,
                                height: 16,
                                fontSize: '0.58rem',
                                fontWeight: 700,
                                bgcolor: 'rgba(22,163,74,0.12)',
                                color: '#16A34A',
                              }}
                            />
                          )}
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                      <Chip
                        label={`Seat ${s.seatNo}`}
                        size="small"
                        sx={{
                          fontSize: '0.62rem',
                          fontWeight: 700,
                          height: 20,
                          bgcolor: 'action.selected',
                        }}
                      />
                      <Chip
                        icon={<QrCode2 sx={{ fontSize: '0.7rem !important' }} />}
                        label={s.plan}
                        size="small"
                        sx={{
                          fontSize: '0.58rem',
                          fontWeight: 600,
                          height: 18,
                          color: 'text.secondary',
                        }}
                      />
                    </Box>
                  </ListItem>
                </motion.div>
              ))}
            </AnimatePresence>
          </List>
        </CardContent>
      </Card>
    </Box>
  );
}

export default DemoSessionsTab;
